import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { getToken } from '../utils/auth';
import { useMenu } from './MenuContext';

// #287/#900: Menü bazlı aksiyon hakları. ActionType enum ile birebir.
export const ACTION = { ADD: 1, UPDATE: 2, DELETE: 3, COPY: 4, VIEW: 17 };

// Backend sözleşmesi:
//   { allActionsAllowed: bool, menuActions: { "<menuId>": [1,2,17], ... } }
// - allActionsAllowed=true → her menü/aksiyon açık (ADMIN veya default-open fallback).
// - allActionsAllowed=false → menuActions'ta VAR OLAN menü = yalnız listelenen aksiyonlar;
//   EKSİK anahtar = default-open (o menüde kısıt tanımlanmamış).
const PermissionContext = createContext({
  allActionsAllowed: true,
  menuActions: {},
  loading: false,
  hasLoaded: false,
  reload: () => {},
});

export function PermissionProvider({ children }) {
  const [allActionsAllowed, setAllActionsAllowed] = useState(true);
  const [menuActions, setMenuActions] = useState({});
  const [loading, setLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);

  const fetchRights = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setAllActionsAllowed(true);
      setMenuActions({});
      setHasLoaded(false);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch('/api/Menu/GetMyMenuRights', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const body = await res.json();
        const data = body?.data ?? body ?? {};
        setAllActionsAllowed(data.allActionsAllowed !== false);
        // Anahtarları Number'a normalize et.
        const map = {};
        const ma = data.menuActions || {};
        Object.keys(ma).forEach((k) => { map[Number(k)] = (ma[k] || []).map(Number); });
        setMenuActions(map);
      } else {
        // Güvenli taraf: hata → default-open (mevcut davranışı bozma).
        setAllActionsAllowed(true);
        setMenuActions({});
      }
    } catch {
      setAllActionsAllowed(true);
      setMenuActions({});
    } finally {
      setLoading(false);
      setHasLoaded(true);
    }
  }, []);

  useEffect(() => {
    fetchRights();
    const onLogin = () => fetchRights();
    const onLogout = () => { setAllActionsAllowed(true); setMenuActions({}); setHasLoaded(false); };
    window.addEventListener('auth:login', onLogin);
    window.addEventListener('auth:logout', onLogout);
    return () => {
      window.removeEventListener('auth:login', onLogin);
      window.removeEventListener('auth:logout', onLogout);
    };
  }, [fetchRights]);

  const value = useMemo(
    () => ({ allActionsAllowed, menuActions, loading, hasLoaded, reload: fetchRights }),
    [allActionsAllowed, menuActions, loading, hasLoaded, fetchRights],
  );

  return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>;
}

export function usePermissions() {
  return useContext(PermissionContext);
}

// Verilen path için efektif menü id'sini menü listesinden çözer (en uzun eşleşen path öneki).
function resolveMenuId(menuItems, pathname) {
  if (!Array.isArray(menuItems) || !pathname) return null;
  let best = null;
  let bestLen = -1;
  for (const m of menuItems) {
    // GetMyMenu (DB) menüleri route'u PascalCase `Path` ile döndürür; enjekte/hardcoded
    // öğeler `path` kullanır. Sidebar da `m.path ?? m.Path` ile iki casing'i okur — burada da
    // ikisini de dene, aksi halde DB menüleri için menuId hiç çözülemez ve gate default-open kalır.
    const p = m?.path ?? m?.Path;
    if (!p || p === '#') continue;
    if (pathname === p || pathname.startsWith(p + '/')) {
      if (p.length > bestLen) { best = m; bestLen = p.length; }
    }
  }
  return best ? Number(best.id ?? best.Id) : null;
}

/**
 * Aktif route (veya verilen path) için aksiyon izinlerini döndürür.
 * Kısıt yoksa / menü çözülemezse / haklar yüklenmediyse → default-open (hepsi true),
 * böylece mevcut davranış korunur ve yükleme anında flash olmaz.
 */
export function usePageActions(pathOverride) {
  const { allActionsAllowed, menuActions, hasLoaded } = usePermissions();
  const { menuItems } = useMenu();
  const location = useLocation();
  const pathname = pathOverride || location?.pathname || '';

  return useMemo(() => {
    const menuId = resolveMenuId(menuItems, pathname);
    const allow = (a) => {
      if (!hasLoaded || allActionsAllowed) return true;
      if (menuId == null) return true;                  // menü çözülemedi → kısıtlama yok
      const arr = menuActions[menuId];
      if (!arr) return true;                            // eksik anahtar → default-open
      return arr.includes(a);
    };
    return {
      menuId,
      canAdd: allow(ACTION.ADD),
      canEdit: allow(ACTION.UPDATE),
      canDelete: allow(ACTION.DELETE),
      canCopy: allow(ACTION.COPY),
      canView: allow(ACTION.VIEW),
    };
  }, [menuItems, pathname, allActionsAllowed, menuActions, hasLoaded]);
}
